import { useCallback, useEffect, useRef, useState } from "react";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { fetchQuestionRows } from "../../lib/findingsTriage.js";
import { BACKEND_STANDARDS_MODEL, PREVIEW_MAX_FINDINGS, openPreviewModel, previewStandard } from "../../lib/standardPreview.js";
import { SEVERITY_COLOR } from "../../lib/traceReview.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { CAPS_CHIP, FILE_LABEL, RUNG_CHIP } from "./triageParts.jsx";

const TEXT = "text-[11px] leading-[1.35] text-[color:var(--muted)]";
// Dashed in ink and set on the page's own background, unlike the form around
// it: nothing in here is stored, and the box says so before the words do.
const PREVIEW_BOX = "grid gap-[7px] rounded-[2px] border border-dashed border-[color:var(--ink)] bg-[color:var(--bg)] px-[10px] py-[8px]";
const LINK = "justify-self-start cursor-pointer border-0 bg-transparent p-0 text-[11px] leading-[1.35] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]";
const MORE = "cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[2px] text-[10px] leading-[16px] tracking-[0.06em] uppercase text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:cursor-default disabled:opacity-40";
// Rows a group shows before it is opened in full.
const SHOWN = 6;
// The most one try asks in one go: the tab asks them, and pays for them.
export const REST_MAX = 500;

const OUTCOME = {
  met: { label: "met", chip: RUNG_CHIP.look_now },
  // A yes the second ask did not confirm is saved as not met, and the Triage
  // tab never lists it: a try says so in the same words.
  unconfirmed: { label: "not met", chip: RUNG_CHIP.routine },
  not_met: { label: "not met", chip: RUNG_CHIP.routine },
  error: { label: "not asked", chip: "border-dashed border-[color:var(--line)] text-[color:var(--muted-soft)]" },
};
const usd = (n) => `$${(Number(n) || 0).toFixed(n >= 1 ? 2 : 3)}`;
export const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

// A wording tried in this browser on findings already verified in the window,
// asked as the backend asks a standard, by the model the backend asks where the
// chat's provider carries it. Nothing is written. `ids` are the window's true
// or partly true findings, newest first; the newest batch is asked first, and
// each `more` asks the next ones of the same wording, adding to the results.
// `was(id)`, for a new version of a saved standard, is what the version in use
// says of a finding now ("met", "unconfirmed", "not_met", or null for never
// asked); a new standard has nothing to compare with and passes none.
export function useStandardTry({ ids = [], runWindow = null, was = null }) {
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0, costUsd: 0 });
  const [model, setModel] = useState(null);
  const [tried, setTried] = useState({ text: "", standardId: "draft" });
  // How far down `ids` the asks have reached, and what earlier batches cost.
  const asked = useRef(0);
  const spent = useRef({ done: 0, costUsd: 0 });
  const controller = useRef(null);
  const wasRef = useRef(was);
  wasRef.current = was;
  useEffect(() => () => controller.current?.abort(), []);
  const key = ids.join(",");

  const ask = useCallback(async (text, standardId, count, { fresh }) => {
    controller.current?.abort();
    const ctl = new AbortController();
    controller.current = ctl;
    if (fresh) {
      asked.current = 0;
      spent.current = { done: 0, costUsd: 0 };
      setResults([]);
      setModel(null);
    }
    const from = asked.current;
    const chosen = ids.slice(from, from + count);
    if (!chosen.length) return;
    setError("");
    setTried({ text, standardId });
    setPhase("reading");
    const base = spent.current;
    setProgress({ done: base.done, total: base.done + chosen.length, costUsd: base.costUsd });
    try {
      const [rowsById, opened] = await Promise.all([
        fetchQuestionRows({ ids: chosen, reviewWindow: runWindow, signal: ctl.signal }),
        openPreviewModel(),
      ]);
      if (ctl.signal.aborted) return;
      setModel(opened);
      const rows = chosen.map((id) => rowsById[id]).filter(Boolean);
      asked.current = from + chosen.length;
      if (!rows.length) {
        setPhase(fresh ? "failed" : "done");
        if (fresh) setError("None of these findings' verified records could be read.");
        return;
      }
      setPhase("asking");
      const done = await previewStandard({
        standard: { standardId, text }, rows, complete: opened.complete, signal: ctl.signal,
        onAnswer: (a) => {
          const id = String(a.row.finding_id);
          setResults((cur) => [...cur, {
            id, title: String(a.row.finding_title || ""), severity: String(a.row.finding_severity || ""),
            outcome: a.outcome, why: a.why, was: wasRef.current?.(id) ?? null,
          }]);
        },
        onProgress: (p) => setProgress({ done: base.done + p.done, total: base.done + p.total, costUsd: base.costUsd + p.costUsd }),
      });
      spent.current = { done: base.done + done.done, costUsd: base.costUsd + done.costUsd };
      setPhase(done.stopped ? "stopped" : "done");
    } catch (e) {
      if (ctl.signal.aborted) return;
      setPhase("failed");
      setError(String(e?.serverMessage || e?.message || e || "the preview failed").slice(0, 300));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, runWindow]);

  // `count` is how many of the newest to ask first: a sample, or the window.
  const run = useCallback((wording, { standardId = "draft", count = PREVIEW_MAX_FINDINGS } = {}) => {
    const text = String(wording || "").trim();
    if (text && ids.length) ask(text, standardId, Math.min(count, REST_MAX), { fresh: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ask, key]);
  const more = useCallback((count = PREVIEW_MAX_FINDINGS) => {
    if (tried.text) ask(tried.text, tried.standardId, count, { fresh: false });
  }, [ask, tried]);
  const stop = useCallback(() => { controller.current?.abort(); setPhase("stopped"); }, []);
  const reset = useCallback(() => {
    controller.current?.abort();
    asked.current = 0;
    spent.current = { done: 0, costUsd: 0 };
    setPhase("idle"); setResults([]); setError(""); setTried({ text: "", standardId: "draft" });
  }, []);
  const running = phase === "reading" || phase === "asking";
  return {
    run, more, stop, reset, running, phase, error, results, progress, model, tried: tried.text,
    size: Math.min(PREVIEW_MAX_FINDINGS, ids.length), of: ids.length, left: Math.max(0, ids.length - asked.current),
    perAnswer: progress.done ? progress.costUsd / progress.done : 0,
  };
}

// A result's place: against the version in use for a new version of a saved
// standard, by its own answer for a new standard.
const flagged = (o) => o === "met";
function groupOf(r, compare) {
  if (r.outcome === "error") return "error";
  if (!compare) return flagged(r.outcome) ? "flag" : "same";
  if (flagged(r.outcome) && !flagged(r.was)) return "flag";
  if (!flagged(r.outcome) && flagged(r.was)) return "stop";
  return "same";
}
const GROUPS = {
  compare: [["flag", "Newly flagged", true], ["stop", "No longer flagged", true], ["same", "Unchanged", false], ["error", "Couldn\u2019t ask", false]],
  own: [["flag", "Would flag", true], ["same", "Not flagged", false], ["error", "Couldn\u2019t ask", false]],
};

const Chip = ({ outcome }) => <span className={`${CAPS_CHIP} ${OUTCOME[outcome]?.chip || OUTCOME.not_met.chip}`}>{OUTCOME[outcome]?.label || "not met"}</span>;

function Group({ id, label, rows, openAtFirst, compare }) {
  const [open, setOpen] = useState(openAtFirst);
  const [all, setAll] = useState(false);
  if (!rows.length) return null;
  const shown = all ? rows : rows.slice(0, SHOWN);
  return (
    <div data-lite-std-try-group={id} className="grid gap-[4px]">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex cursor-pointer items-baseline gap-[6px] border-0 bg-transparent p-0 text-left text-[11px] leading-[1.35] text-[color:var(--ink)]">
        <span aria-hidden="true" className="w-[8px] text-[color:var(--muted)]">{open ? "\u25be" : "\u25b8"}</span>
        <span className="font-medium">{label}</span>
        <span className="text-[color:var(--muted)]">{rows.length.toLocaleString()}</span>
      </button>
      {open ? (
        <>
          <ul className="m-0 grid list-none gap-[4px] p-0">
            {shown.map((r) => (
              <li key={r.id} data-lite-std-try-row={r.outcome} className={`grid items-start gap-x-[8px] ${compare ? "grid-cols-[150px_minmax(0,1fr)]" : "grid-cols-[74px_minmax(0,1fr)]"}`}>
                <span className="flex items-center gap-[3px]">
                  {compare ? <><Chip outcome={r.was || "not_met"} /><span aria-label="now" className="text-[10px] text-[color:var(--muted)]">{"\u2192"}</span></> : null}
                  <Chip outcome={r.outcome} />
                </span>
                <span className="grid min-w-0 gap-[1px]">
                  <OverflowTooltip text={r.title} className="min-w-0 truncate text-[12px] leading-[1.35] text-[color:var(--ink)]" />
                  {r.why && id !== "same" ? <span className="text-[11px] leading-[1.35] text-[color:var(--muted)] [overflow-wrap:anywhere]">{r.why}</span> : null}
                </span>
              </li>
            ))}
          </ul>
          {rows.length > SHOWN ? (
            <button type="button" data-lite-std-try-all={id} onClick={() => setAll((v) => !v)} className={LINK} style={{ marginLeft: compare ? 158 : 82 }}>
              {all ? "Show fewer" : `Show all ${rows.length.toLocaleString()}`}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// What a try found, under the form: for a new version of a saved standard,
// what it would change against the version in use (`against`, e.g. "v2");
// for a new standard, what it would flag. Then how sure the met rate is so
// far, each group of findings, and the next batch to ask. `text` is the
// wording as it stands, so answers to an older wording say so.
export function StandardTryResults({ trial, text = "", windowName = "this window", against = null }) {
  const { phase, results, progress, model, error, tried, running, left, of } = trial;
  if (phase === "idle") return null;
  const compare = Boolean(against);
  const kinds = compare ? GROUPS.compare : GROUPS.own;
  const groups = Object.fromEntries(kinds.map(([id]) => [id, []]));
  for (const r of results) groups[groupOf(r, compare)].push(r);
  const answered = results.filter((r) => r.outcome !== "error");
  const met = answered.filter((r) => r.outcome === "met").length;
  // The met rate so far, with a 95% margin, so a reader knows when to stop.
  const rate = answered.length ? met / answered.length : 0;
  const margin = answered.length ? 1.96 * Math.sqrt((rate * (1 - rate)) / answered.length) : 0;
  const settled = answered.length >= 50 && margin < 0.05;
  const stale = Boolean(results.length) && String(text).trim() !== tried;
  const perAnswer = progress.done ? progress.costUsd / progress.done : 0;
  const rest = Math.min(left, REST_MAX);
  const share = (n) => `${results.length ? (n / results.length) * 100 : 0}%`;

  return (
    <div data-lite-std-try="" className={PREVIEW_BOX}>
      <div className="flex flex-wrap items-baseline gap-x-[8px] gap-y-[4px]">
        <span data-lite-std-try-badge="" className={`${CAPS_CHIP} border-dashed border-[color:var(--ink)] text-[color:var(--ink)]`}>preview{" · "}not saved</span>
        <span data-lite-std-try-of="" className={FILE_LABEL}>Tried on {progress.total.toLocaleString()} of {plural(of, "finding")}{windowName === "all time" ? "" : ` from ${windowName}`}</span>
        <span className={`${TEXT} ml-auto`}>
          {running && phase === "reading" ? "Reading the verified findings\u2026" : running ? <><strong className="font-medium text-[color:var(--ink)]">{progress.done}</strong> of {progress.total} asked{" \u00b7 "}</> : null}
          {usd(progress.costUsd)}
        </span>
      </div>
      {model ? (
        <span data-lite-std-try-model="" className={NOTE_CLASS}>
          Asked from this browser of {model.modelId || "the chat's task model"}{model.sameAsBackend ? ", the model the backend asks" : `; the backend asks ${BACKEND_STANDARDS_MODEL}, so its answers may differ`}. The list and the backend&rsquo;s answers stay as they are.
        </span>
      ) : null}
      {running && phase === "asking" ? (
        <div className="flex h-[3px] overflow-hidden rounded-[2px] bg-[color:var(--line-soft)]" role="img" aria-label={`${progress.done} of ${progress.total} asked`}>
          <div className="h-full bg-[color:var(--ink)] transition-[width] duration-300" style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }} />
        </div>
      ) : null}
      {error ? <span role="alert" data-lite-std-try-error="" className={TEXT}>Could not try this wording: {error}</span> : null}
      {results.length ? (
        <>
          <div className="grid gap-[3px]">
            <span data-lite-std-try-summary="" className="text-[12px] leading-[1.35] text-[color:var(--ink)]">
              {compare ? (
                <>
                  Would newly flag <strong className="font-medium">{groups.flag.length}</strong>{" \u00b7 "}stop flagging <strong className="font-medium">{groups.stop.length}</strong>{" \u00b7 "}{groups.same.length} unchanged
                </>
              ) : (
                <>Would flag <strong className="font-medium">{groups.flag.length}</strong>{" \u00b7 "}{groups.same.length} not flagged</>
              )}
              {groups.error.length ? <span className="text-[color:var(--muted)]">{` \u00b7 ${groups.error.length} couldn\u2019t be asked`}</span> : null}
              {compare ? <span className="text-[color:var(--muted)]">{`, against ${against}`}</span> : null}
            </span>
            <div className="flex h-[5px] overflow-hidden rounded-[2px] bg-[color:var(--line-soft)]" aria-hidden="true">
              <div style={{ width: share(groups.flag.length), background: SEVERITY_COLOR.danger }} />
              {compare ? <div style={{ width: share(groups.stop.length), background: "var(--warn, #c08a2d)" }} /> : null}
            </div>
            {answered.length ? (
              <span data-lite-std-try-rate="" className="text-[10px] leading-[1.35] text-[color:var(--muted)]">
                {`${Math.round(rate * 100)}% met so far (\u00b1${Math.max(1, Math.round(margin * 100))} pts)`}{settled ? " \u00b7 settling" : ""}
                {phase === "stopped" ? ` \u00b7 stopped after ${progress.done} of ${progress.total}` : ""}
              </span>
            ) : null}
          </div>
          {stale ? <span data-lite-std-try-stale="" className={NOTE_CLASS}>These answers are for the wording you tried; it has changed since. Try it again to ask the new wording.</span> : null}
          <div className="grid gap-[8px]">
            {kinds.map(([id, label, openAtFirst]) => (
              <Group key={id} id={id} label={label} rows={groups[id]} openAtFirst={openAtFirst} compare={compare && (id === "flag" || id === "stop")} />
            ))}
          </div>
          {!stale && left > 0 ? (
            <div data-lite-std-try-more="" className="flex flex-wrap items-center gap-[6px] border-t border-[color:var(--line-soft)] pt-[7px]">
              <button type="button" data-lite-std-try-next="" disabled={running} onClick={() => trial.more(PREVIEW_MAX_FINDINGS)} className={MORE}>
                Try {Math.min(PREVIEW_MAX_FINDINGS, left)} more
              </button>
              {left > PREVIEW_MAX_FINDINGS ? (
                <button type="button" data-lite-std-try-rest="" disabled={running} onClick={() => trial.more(rest)} className={MORE}>
                  {rest < left ? `Try ${rest.toLocaleString()} more` : `Try the rest \u00b7 ${left.toLocaleString()}`}{perAnswer ? ` \u00b7 ~${usd(perAnswer * rest)}` : ""}
                </button>
              ) : null}
              <span className={TEXT}>Each batch adds to these results.</span>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
